import { useState, useEffect, useCallback, useRef } from 'react';
import { message } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { createSiteAction, uploadSiteImageAction, updateSiteAction, checkMapExistsAction } from '../../../store/siteSlice';
import { getBuildingAction } from '../../../store/floorSlice';
import { transformData, reverseTransformData } from '../../../shared/utils';
import axios from 'axios';
import debounce from 'lodash.debounce';
import { convertToBase64, getBase64 } from '../utils';
export const useSiteOnboard = () => {
    // ========== REDUX SELECTORS ==========
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const siteIdStepper = useSelector((state) => state?.site?.createSite?.response?.data?.siteId);
    const step = useSelector((state) => state?.site?.checkSite?.response?.data?.currentStep);
    const siteIdCheckSite = useSelector((state) => state?.site?.checkSite?.response?.data?.siteId);
    const mapIdAuth = useSelector((state) => state?.auth?.mapId);
    const siteName = useSelector((state) => state?.auth?.siteName);
    const checkSiteResponse = useSelector((state) => state?.site?.checkSite?.response?.data);
    const stateLoading = useSelector((state) => state?.site?.checkSite?.loading);
    const createSiteLoading = useSelector((state) => state?.site?.createSite?.loading);
    const updateSiteLoading = useSelector((state) => state?.site?.updateSite?.loading);
    const uploadSiteImageLoading = useSelector((state) => state?.site?.uploadSiteImage?.loading);
    const getBuildingLoading = useSelector((state) => state?.floors?.buildings?.loading);
    // ========== LOCAL STATE ==========
    const [loading, setLoading] = useState(false);
    const [validationError, setValidationError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [imageCheck, setImageCheck] = useState(false);
    const [imageUrl, setImageUrl] = useState(null);
    const [imageBase64, setImageBase64] = useState();
    const [currentStep, setCurrentStep] = useState(-1);
    const [utcOffset, setUtcOffset] = useState('');
    const [initialLocationHours, setInitialLocationHours] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [initialValuesStep1, setInitialValuesStep1] = useState({
        siteName: siteName || '',
        siteType: checkSiteResponse?.type || '',
        phoneNumber: checkSiteResponse?.telephone || '',
        siteLink: checkSiteResponse?.link || '',
        siteLanguages: ['en'], // Default to English
    });
    const [optionsAddress, setOptionsAddress] = useState([]);
    const [loadingAddress, setLoadingAddress] = useState(false);
    const latestQueryRef = useRef('');
    // ====================SIDE EFFECTS ====================
    // Set current step from Redux
    useEffect(() => {
        if (step !== undefined) {
            setCurrentStep(step);
        }
    }, [step]);
    // Set UTC offset for site
    useEffect(() => {
        const getUtcOffset = () => {
            const offset = new Intl.DateTimeFormat('en-US', {
                timeZoneName: 'short',
            })
                .formatToParts(new Date())
                .find((part) => part.type === 'timeZoneName')?.value;
            if (offset) {
                const formattedOffset = offset.replace(/GMT/g, '');
                setUtcOffset(formattedOffset);
            }
        };
        getUtcOffset();
    }, []);
    // Set initial business hours from site response
    useEffect(() => {
        if (checkSiteResponse) {
            setInitialLocationHours(reverseTransformData(checkSiteResponse?.operationHours));
        }
    }, [checkSiteResponse]);
    // Debounced search for address autocomplete
    const searchAddressDebounced = useCallback(debounce(async (searchQuery) => {
        latestQueryRef.current = searchQuery; // Track the latest query
        if (searchQuery) {
            setLoadingAddress(true);
            try {
                const response = await axios.get(`https://nominatim.openstreetmap.org/search`, {
                    params: {
                        q: searchQuery,
                        format: 'json',
                    },
                });
                // Only update if this is still the latest query
                if (latestQueryRef.current === searchQuery) {
                    const data = response.data;
                    if (data && data.length > 0) {
                        const newOptions = data.map((item) => ({
                            value: item.display_name,
                            label: item.display_name,
                            data: {
                                lat: parseFloat(item.lat),
                                lon: parseFloat(item.lon),
                                address: item.display_name,
                                bound: item?.boundingbox?.map(Number),
                            }
                        }));
                        setOptionsAddress(newOptions);
                    }
                    else {
                        setOptionsAddress([]);
                        message.warning('No locations found.');
                    }
                }
            }
            catch (error) {
                if (latestQueryRef.current === searchQuery) {
                    setOptionsAddress([]);
                    message.error('An error occurred while fetching location suggestions.');
                }
            }
            finally {
                if (latestQueryRef.current === searchQuery) {
                    setLoadingAddress(false);
                }
            }
        }
        else {
            setOptionsAddress([]);
        }
    }, 300), []);
    // ==================== HANDLERS ====================
    // Handle image file change for site icon
    const handleImageChange = async (info) => {
        if (info.file.status === 'uploading') {
            setLoading(true);
            return;
        }
        if (info.file.status === 'done' && info.file.originFileObj) {
            try {
                const base64 = await convertToBase64(info.file.originFileObj);
                setImageBase64(base64.split(',')[1]);
            }
            catch (error) {
                if (error instanceof Error) {
                    message.error('Error converting file to base64: ' + error.message);
                }
                else {
                    message.error('Unknown error occurred while converting file to base64');
                }
            }
            getBase64(info.file.originFileObj, (url) => {
                setLoading(false);
                setImageUrl(url);
            });
        }
    };
    // Check if form values have changed
    const hasChanges = (initialValues, newValues) => {
        return JSON.stringify(initialValues) !== JSON.stringify(newValues);
    };
    // Handle Step 1 form submission (site details)
    const handleFormSubmit = (values) => {
        if (!hasChanges(initialValuesStep1, values)) {
            setCurrentStep(currentStep + 1);
            return;
        }
        setInitialValuesStep1(values);
        // Prepare languages array for API
        const languages = values.siteLanguages?.map((item) => {
            const selectedLanguages = [
                { value: 'en', label: 'English' },
                { value: 'es', label: 'Spanish' },
                { value: 'fr', label: 'French' },
                { value: 'de', label: 'German' },
                { value: 'it', label: 'Italian' },
                { value: 'pt', label: 'Portuguese' },
                { value: 'ru', label: 'Russian' },
                { value: 'zh', label: 'Chinese' },
                { value: 'ja', label: 'Japanese' },
                { value: 'ko', label: 'Korean' },
                { value: 'ar', label: 'Arabic' },
            ].find((option) => option?.value === item || item?.value);
            return {
                name: selectedLanguages?.label,
                code: selectedLanguages?.value,
                enabled: true,
                default: true,
            };
        });
        // Prepare data for API
        const data = {
            mapId: mapIdAuth ? mapIdAuth : '',
            siteName: values.siteName,
            type: values.siteType,
            telephone: values.phoneNumber,
            link: values.siteLink,
            languages: languages,
            tzId: Intl.DateTimeFormat().resolvedOptions().timeZone,
            utcOffset: utcOffset,
        };
        // If onboarding, create site; else, update site
        if (checkSiteResponse?.currentStep === undefined) {
            dispatch(createSiteAction(data)).then(() => {
                setCurrentStep(currentStep + 1);
                dispatch(checkMapExistsAction(mapIdAuth));
            });
        }
        else {
            const stepOneData = {
                type: values.siteType,
                telephone: values.phoneNumber,
                link: values.siteLink,
                languages: languages,
            };
            handleUpdateStep1(stepOneData);
        }
    };
    // Handle Step 1 update (site details)
    const handleUpdateStep1 = async (data) => {
        try {
            await dispatch(updateSiteAction({ siteId: checkSiteResponse?.siteId, data })).then(() => {
                dispatch(checkMapExistsAction(mapIdAuth)).then((res) => {
                    if (res?.type === 'site/check /fulfilled') {
                        setCurrentStep(currentStep + 1);
                    }
                });
            });
        }
        catch (error) {
            console.error('Error updating step 1:', error);
        }
    };
    // Handle Step 2 update (icon upload)
    const handleUpdateStep2 = async () => {
        const data = {
            currentStep: checkSiteResponse?.currentStep === 0 ? 1 : checkSiteResponse?.currentStep,
        };
        try {
            await dispatch(updateSiteAction({ siteId: checkSiteResponse?.siteId, data })).then(() => {
                dispatch(checkMapExistsAction(mapIdAuth));
                setCurrentStep(currentStep + 1);
                setImageUrl(null);
            });
        }
        catch (error) {
            console.error('Error updating step 2:', error);
        }
    };
    // Handle Step 3 update (business hours)
    const handleUpdateStep3 = async (data) => {
        try {
            await dispatch(updateSiteAction({ siteId: checkSiteResponse?.siteId, data })).then(() => {
                dispatch(checkMapExistsAction(mapIdAuth));
                setCurrentStep(currentStep + 1);
            });
        }
        catch (error) {
            console.error('Error updating step 3:', error);
        }
    };
    // Handle image upload for site icon
    const handleImageUpload = () => {
        setImageCheck(true);
        const siteId = siteIdCheckSite || siteIdStepper;
        if (imageBase64 && siteId && imageUrl !== null) {
            setImageCheck(false);
            const data = {
                image: imageBase64,
            };
            dispatch(uploadSiteImageAction({ siteId, data })).then(() => {
                handleUpdateStep2();
            });
        }
        else if (checkSiteResponse?.imageUrl && !imageUrl) {
            setImageCheck(false);
            setCurrentStep(currentStep + 1);
        }
    };
    // Handle Step 3 form submission (business hours)
    const handleFormSubmit3 = (values) => {
        const formattedData = transformData(values);
        if (formattedData) {
            const data = {
                operationHours: formattedData,
                currentStep: checkSiteResponse?.currentStep === 1 ? 2 : checkSiteResponse?.currentStep,
            };
            if (hasChanges(initialLocationHours, values)) {
                handleUpdateStep3(data);
            }
            else {
                setCurrentStep(currentStep + 1);
            }
        }
        else {
            message.error('Something went wrong');
        }
    };
    // Handle address selection (store for later update)
    const handleAddressSelect = async (location) => {
        setSelectedLocation(location);
    };
    // Handle Step 4 (location) continue button
    const handleFourthStep = async () => {
        if (selectedLocation && siteIdCheckSite) {
            try {
                await dispatch(updateSiteAction({
                    siteId: checkSiteResponse?.siteId,
                    data: {
                        latitude: selectedLocation?.lat,
                        longitude: selectedLocation?.lon,
                        address: selectedLocation?.address,
                        bound: selectedLocation?.bound,
                    },
                })).then((response) => {
                    if (response?.payload) {
                        dispatch(getBuildingAction(siteIdCheckSite)).then(() => {
                            navigate('/building');
                        });
                    }
                });
            }
            catch (error) {
                console.error('Error updating address:', error);
                message.error('Something went wrong');
            }
        }
        else {
            message.error('Please select a location first');
        }
    };
    // Handle business hours value change (clear error if any day is open)
    const handleValuesChange = (changedValues, allValues) => {
        const isAnyDayOpen = Object.keys(allValues).some((day) => allValues[day]?.isOpen);
        if (isAnyDayOpen) {
            setValidationError(null);
        }
    };
    // ==================== LOADING STATES FOR STEPS ====================
    // Step-specific loading states for each step
    const step1Loading = createSiteLoading || updateSiteLoading;
    const step2Loading = uploadSiteImageLoading || updateSiteLoading;
    const step3Loading = updateSiteLoading;
    const step4Loading = updateSiteLoading || getBuildingLoading;
    // ==================== RETURN HOOK API ====================
    return {
        // State
        loading,
        validationError,
        success,
        imageCheck,
        imageUrl,
        imageBase64,
        currentStep,
        initialLocationHours,
        initialValuesStep1,
        optionsAddress,
        loadingAddress,
        stateLoading,
        step1Loading,
        step2Loading,
        step3Loading,
        step4Loading,
        checkSiteResponse,
        siteIdCheckSite,
        siteIdStepper,
        // Handlers
        handleFormSubmit,
        handleImageChange,
        handleImageUpload,
        handleFormSubmit3,
        handleAddressSelect,
        handleFourthStep,
        handleValuesChange,
        searchAddressDebounced,
        setCurrentStep,
        setValidationError,
    };
};
